import React, { useCallback, useState } from "react";
import { Redirect, useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "locales";
import useAuthUser from "hooks/useAuthUser";
import providers from "lib/providers";
import { parseErrors } from "lib/requestHelpers";
import { Routes } from "../../router/config";
import "./style.scss";

type AuthMode = "login" | "signup";

interface FieldErrors {
  email?: string;
  password?: string;
  screenName?: string;
  general?: string;
}

function PageAuth() {
  const { t } = useTranslation();
  const { user } = useAuthUser();
  const history = useHistory();
  const location = useLocation();
  const initialMode: AuthMode = location.pathname.startsWith("/signup") ? "signup" : "login";
  const [mode, setMode] = useState<AuthMode>(initialMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [screenName, setScreenName] = useState("");
  const [errors, setErrors] = useState<FieldErrors>({});
  const [loading, setLoading] = useState(false);

  const validate = useCallback((): boolean => {
    const errs: FieldErrors = {};
    if (!email.trim()) errs.email = t("Email is required");
    if (!password) errs.password = t("Password is required");
    if (mode === "signup" && !screenName.trim()) errs.screenName = t("Name is required");
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }, [email, password, screenName, mode, t]);

  const onSubmit = useCallback(async (e?: React.FormEvent) => {
    // Prevent the default form POST — we handle submission via the
    // providers API. BUT we still need a real <form> wrapper so iOS
    // Safari / Keychain / 1Password / Chrome Password Manager detect
    // this as a login form and offer to save the credentials. A div
    // with autoComplete inputs is NOT enough — password managers
    // specifically look for a <form> element that emits a submit
    // event with a password field inside.
    e?.preventDefault();
    if (!validate()) return;
    setLoading(true);
    setErrors({});
    try {
      if (mode === "login") {
        await providers.LoginProvider.login({ email, password });
      } else {
        await providers.LoginProvider.join({
          data: { email, password, screenName, role: "ROLE_CLIENT" },
        });
      }
      history.push(Routes.messages);
    } catch (e) {
      // parseErrors is typed for ErrorDTO[] shape but the caught value is
      // always `unknown` under strict mode — cast once at the boundary.
      const parsed = parseErrors(e as never);
      if (parsed && typeof parsed === "object") {
        const fieldErrs: FieldErrors = {};
        if (Array.isArray(parsed)) {
          fieldErrs.general = parsed.map((p: { message?: string }) => p.message || String(p)).join(". ");
        } else {
          fieldErrs.general = String(parsed);
        }
        setErrors(fieldErrs);
      } else {
        setErrors({ general: t("Something went wrong. Please try again.") });
      }
    } finally {
      setLoading(false);
    }
  }, [mode, email, password, screenName, validate, history, t]);

  if (user) return <Redirect to={Routes.messages} />;

  return (
    <div className="page-auth">
      <div className="page-auth__card">
        <div className="page-auth__logo">
          <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H5.17L4 17.17V4h16v12z" />
            <path d="M7 9h2v2H7zm4 0h2v2h-2zm4 0h2v2h-2z" />
          </svg>
        </div>
        <h1 className="page-auth__title">Scrile Messenger</h1>
        <p className="page-auth__subtitle">
          {mode === "login"
            ? t("Sign in to your account to continue")
            : t("Create an account to get started")}
        </p>

        {errors.general && <div className="page-auth__error-general">{errors.general}</div>}

        {/*
          Real <form> element is REQUIRED for iOS Safari Keychain /
          1Password / Chrome Password Manager to offer "Save Password".
          Keyed by mode so switching login ↔ signup remounts the form —
          this prevents the browser from associating a login-mode submit
          with signup-only fields (and vice versa) in its autofill
          heuristics. Credentials saved from the login form are tied to
          this form instance; a fresh form on signup keeps the suggestion
          logic clean.
        */}
        <form
          key={mode}
          className="page-auth__form"
          onSubmit={onSubmit}
          method="post"
          action="#"
          autoComplete="on"
          noValidate
        >
          {mode === "signup" && (
            <div className="page-auth__field">
              <input
                type="text"
                name="name"
                placeholder={t("Your name")}
                value={screenName}
                onChange={(e) => setScreenName(e.target.value)}
                autoComplete="name"
              />
              {errors.screenName && <div className="page-auth__error">{errors.screenName}</div>}
            </div>
          )}
          <div className="page-auth__field">
            {/*
              type="email" + autoComplete="username" is the combination
              iOS Keychain looks for. Using autoComplete="email" alone
              works for some managers but iOS specifically keys on
              "username" for credential association with the password
              field below. inputMode="email" keeps the email keyboard.
            */}
            <input
              type="email"
              name="username"
              placeholder={t("Email")}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="username"
              inputMode="email"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              autoFocus
            />
            {errors.email && <div className="page-auth__error">{errors.email}</div>}
          </div>
          <div className="page-auth__field">
            <input
              type="password"
              name="password"
              placeholder={t("Password")}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete={mode === "login" ? "current-password" : "new-password"}
            />
            {errors.password && <div className="page-auth__error">{errors.password}</div>}
          </div>

          {mode === "login" && (
            <div className="page-auth__forgot">
              <button type="button" onClick={() => history.push(Routes.loginForgot)}>
                {t("Forgot password?")}
              </button>
            </div>
          )}

          {/*
            type="submit" is the other half of the form contract — browser
            password managers watch for submit events from type=submit
            buttons or Enter-in-a-password-field events, both of which
            trigger onSubmit here. type="button" (previous version) would
            never fire the save-password prompt.
          */}
          <button
            className="page-auth__submit-btn"
            disabled={loading}
            type="submit"
          >
            {loading ? "..." : mode === "login" ? t("Log In") : t("Sign Up")}
          </button>
        </form>

        <div className="page-auth__divider">
          <span>{mode === "login" ? t("Don't have an account?") : t("Already have an account?")}</span>
        </div>

        <div className="page-auth__switch">
          {mode === "login" ? (
            <>
              {t("New here?")}
              <button type="button" onClick={() => { setMode("signup"); setErrors({}); }}>
                {t("Create account")}
              </button>
            </>
          ) : (
            <>
              {t("Have an account?")}
              <button type="button" onClick={() => { setMode("login"); setErrors({}); }}>
                {t("Log In")}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default PageAuth;
